import { ChevronDown } from "lucide-react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import {
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"

import { cn } from "@/lib/cn"

/**
 * Un botón que agrupa lo que se puede hacer con lo que tiene debajo, como en len-platform.
 *
 * ⚠ **No sustituye a la acción primaria.** Si una pantalla tiene una cosa que la gente
 * viene a hacer, ésa va fuera, como botón propio; aquí va todo lo demás. Una cabecera
 * con cinco botones sueltos obliga a leerlos todos cada vez, y lo que no se deshace
 * compite en peso con lo que sólo consulta.
 *
 * ⚠ **La línea de ayuda dice la CONSECUENCIA, no un sinónimo del nombre**: es lo que
 * deja elegir leyendo una vez.
 *
 * ⚠ **El panel es de la superficie del tema y no cristal**, como el de la campanita:
 * es contenido que se lee, no marco. El disparador es un `boton-secundario` porque vive
 * dentro de una tarjeta.
 *
 * ⚠ **El panel va en un PORTAL a `body`, con posición fija** (15/10/2026), como la
 * campanita: dentro de una tabla, el `overflow` de su contenedor lo recortaba y el menú
 * de una fila salía cortado (Tesorería → Ingresos). Se ancla al botón al abrir, se
 * recoloca al desplazar o cambiar el tamaño, y se abre hacia ARRIBA si abajo no cabe.
 *
 * Accesibilidad, que es la mitad del trabajo de un menú: al abrir, el foco entra en la
 * primera opción; las flechas recorren las opciones —saltándose las deshabilitadas— e
 * Inicio y Fin van a los extremos; Escape cierra y DEVUELVE el foco al botón, no al
 * principio de la página; Tab y pulsar fuera cierran.
 */

export type OpcionMenu = {
  /** Estable: también es la `key`. */
  id: string
  etiqueta: string
  /** La consecuencia, en una línea. */
  ayuda?: string
  icono?: ReactNode
  onSelect: () => void
  /** En rojo: lo que no se deshace con otro clic no se pulsa por inercia. */
  peligrosa?: boolean
  deshabilitada?: boolean
  /** Una línea encima: aquí empieza otro grupo. */
  separador?: boolean
}

export function MenuAcciones({
  opciones,
  etiqueta = "Acciones",
}: {
  opciones: readonly OpcionMenu[]
  etiqueta?: string
}) {
  const [abierto, setAbierto] = useState(false)
  const [foco, setFoco] = useState(0)
  const caja = useRef<HTMLDivElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  const [ancla, setAncla] = useState<{ top?: number; bottom?: number; right: number } | null>(null)
  const disparador = useRef<HTMLButtonElement | null>(null)
  const items = useRef<(HTMLButtonElement | null)[]>([])
  const idMenu = useId()
  const sinMovimiento = useReducedMotion()

  const activas = opciones.filter((o) => !o.deshabilitada)

  useEffect(() => {
    if (!abierto) return
    // `mousedown` y no `click`: con `click` el menú se cierra después de que el
    // navegador procese la pulsación, y un clic en otro botón lo activaba además.
    const fuera = (e: MouseEvent) => {
      const donde = e.target as Node
      if (caja.current?.contains(donde) || panel.current?.contains(donde)) return
      setAbierto(false)
    }
    document.addEventListener("mousedown", fuera)
    return () => document.removeEventListener("mousedown", fuera)
  }, [abierto])

  useEffect(() => {
    if (!abierto) return
    const colocar = () => {
      const r = disparador.current?.getBoundingClientRect()
      if (!r) return
      const right = Math.max(8, window.innerWidth - r.right)
      const abajo = window.innerHeight - r.bottom
      // Hacia arriba sólo si abajo no cabe un menú corriente y arriba hay más sitio.
      setAncla(
        abajo < 280 && r.top > abajo
          ? { bottom: window.innerHeight - r.top + 6, right }
          : { top: r.bottom + 6, right },
      )
    }
    colocar()
    window.addEventListener("resize", colocar)
    window.addEventListener("scroll", colocar, true)
    return () => {
      window.removeEventListener("resize", colocar)
      window.removeEventListener("scroll", colocar, true)
    }
  }, [abierto])

  // El panel aparece cuando ya se sabe dónde va (`ancla`): antes no hay opción que enfocar.
  const colocado = ancla !== null
  useEffect(() => {
    if (abierto && colocado) items.current[foco]?.focus()
  }, [abierto, colocado, foco])

  if (opciones.length === 0) return null

  function abrir() {
    setFoco(0)
    setAncla(null)
    setAbierto(true)
  }

  function cerrar(devolverFoco: boolean) {
    setAbierto(false)
    if (devolverFoco) disparador.current?.focus()
  }

  function teclas(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault()
      cerrar(true)
      return
    }
    // Tab sigue su camino natural; sólo se cierra detrás para no dejar el panel
    // abierto sobre lo que venga.
    if (e.key === "Tab") {
      cerrar(false)
      return
    }
    const n = activas.length
    if (n === 0) return
    const saltos: Record<string, (i: number) => number> = {
      ArrowDown: (i) => (i + 1) % n,
      ArrowUp: (i) => (i - 1 + n) % n,
      Home: () => 0,
      End: () => n - 1,
    }
    const salto = saltos[e.key]
    if (salto === undefined) return
    e.preventDefault()
    setFoco(salto)
  }

  return (
    <div ref={caja} className="relative shrink-0">
      <button
        ref={disparador}
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={abierto ? idMenu : undefined}
        onClick={() => (abierto ? cerrar(false) : abrir())}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !abierto) {
            e.preventDefault()
            abrir()
          }
        }}
        className="boton-secundario text-sm"
      >
        {etiqueta}
        <ChevronDown
          size={14}
          aria-hidden={true}
          className={cn("transition-transform", abierto && "rotate-180")}
          style={{ color: "var(--fg-muted)" }}
        />
      </button>

      {createPortal(
        <AnimatePresence>
          {abierto && ancla && (
            <motion.div
              ref={panel}
              id={idMenu}
              role="menu"
              aria-label={etiqueta}
              onKeyDown={teclas}
              // Anclado a la derecha del botón, y nunca más ancho que la pantalla: en un
              // teléfono de 320 px, 18 rem desde el borde derecho caben justo. Por encima
              // de un cajón o un modal (`z-50`): el menú puede vivir dentro de uno.
              className="fixed z-[60] w-72 max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-[var(--radius-tarjeta)] border p-1.5"
              style={{
                ...ancla,
                backgroundColor: "var(--surface)",
                borderColor: "var(--border)",
                boxShadow: "var(--elev-3)",
              }}
              initial={sinMovimiento ? false : { opacity: 0, y: ancla.bottom ? 6 : -6 }}
              animate={{ opacity: 1, y: 0 }}
              // La salida al ~65 % de la entrada: lo que se va ya no interesa.
              exit={{
                opacity: 0,
                y: ancla.bottom ? 6 : -6,
                transition: { duration: sinMovimiento ? 0 : 0.09 },
              }}
              transition={{ duration: sinMovimiento ? 0 : 0.14 }}
            >
              {opciones.map((o, i) => {
                const indice = activas.indexOf(o)
                return (
                  <Fragment key={o.id}>
                    {/* Un `<hr>` y no un `div` con `role="separator"`: ya ES un
                      separador, y dentro de un menú es de los pocos hijos que admite. */}
                    {o.separador && i > 0 && (
                      <hr className="mx-1 my-1.5" style={{ borderColor: "var(--border)" }} />
                    )}
                    <button
                      ref={(el) => {
                        if (indice >= 0) items.current[indice] = el
                      }}
                      type="button"
                      role="menuitem"
                      // El foco lo lleva el menú con las flechas, no el tabulador.
                      tabIndex={-1}
                      disabled={o.deshabilitada}
                      onClick={() => {
                        cerrar(false)
                        o.onSelect()
                      }}
                      className={cn(
                        "foco-cristal flex w-full items-start gap-2.5 rounded-[var(--radius-control)] px-3 py-2 text-left text-sm transition-colors",
                        o.deshabilitada
                          ? "cursor-not-allowed opacity-45"
                          : "hover:bg-[var(--surface-elevated)] focus:bg-[var(--surface-elevated)]",
                      )}
                      style={{ color: o.peligrosa ? "var(--destructive)" : "var(--fg)" }}
                    >
                      {o.icono && (
                        <span aria-hidden={true} className="mt-0.5 shrink-0">
                          {o.icono}
                        </span>
                      )}
                      <span className="min-w-0">
                        <span className="block font-medium">{o.etiqueta}</span>
                        {o.ayuda && (
                          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                            {o.ayuda}
                          </span>
                        )}
                      </span>
                    </button>
                  </Fragment>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}
